/**
 * @providesModule CircularProgress
 * 
 */


import React from 'react';
import PropTypes from 'prop-types';
import { Animated, View, ViewStyle } from 'react-native';
import { Svg, Path, G } from 'react-native-svg';

// Helper to convert hex to RGB
function hexToRgb(hex: string): number[] {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map((x: string) => x + x).join('');
  }
  const num = parseInt(hex, 16);
  return [
    (num >> 16) & 255,
    (num >> 8) & 255,
    num & 255,
  ];
}

// Helper to interpolate between two RGB colors
function interpolateColor(color1: number[], color2: number[], t: number): number[] {
  return [
    Math.round(color1[0] + (color2[0] - color1[0]) * t),
    Math.round(color1[1] + (color2[1] - color1[1]) * t),
    Math.round(color1[2] + (color2[2] - color1[2]) * t),
  ];
}

// Helper to convert RGB to hex
function rgbToHex(rgb: number[]): string {
  return (
    '#' +
    rgb
      .map((x: number) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}

interface CircularProgressProps {
  style?: ViewStyle;
  size: number | Animated.Value;
  fill: number;
  width: number;
  backgroundWidth?: number;
  tintColor?: string;
  tintTransparency?: boolean;
  backgroundColor?: string;
  rotation?: number;
  lineCap?: 'butt' | 'round' | 'square';
  fillLineCap?: 'butt' | 'round' | 'square';
  arcSweepAngle?: number;
  children?: (fill: number) => React.ReactNode;
  childrenContainerStyle?: ViewStyle;
  padding?: number;
  renderCap?: (props: { center: { x: number; y: number } }) => React.ReactNode;
  dashedBackground?: { width: number; gap: number };
  dashedTint?: { width: number; gap: number };
  beginColor?: string;
  endColor?: string;
  isProgressWithGradient?: boolean;
  isFillerPadding?:boolean
}

interface CartesianCoordinate {
  x: number;
  y: number;
}

export default class CircularProgress extends React.PureComponent<CircularProgressProps> {
  polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number): CartesianCoordinate {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  }

  circlePath(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
    const start = this.polarToCartesian(x, y, radius, endAngle * 0.9999);
    const end = this.polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y];
    return d.join(' ');
  }

  clampFill = (fill: number): number => Math.min(100, Math.max(0, fill));

  render() {
    const {
      size,
      width,
      backgroundWidth,
      backgroundColor,
      style,
      rotation = CircularProgress.defaultProps.rotation,
      lineCap = CircularProgress.defaultProps.lineCap,
      fillLineCap = lineCap,
      arcSweepAngle = CircularProgress.defaultProps.arcSweepAngle,
      fill,
      children,
      childrenContainerStyle,
      padding = CircularProgress.defaultProps.padding,
      renderCap,
      dashedBackground = CircularProgress.defaultProps.dashedBackground,
      dashedTint = CircularProgress.defaultProps.dashedTint,
      beginColor = CircularProgress.defaultProps.beginColor,
      endColor = CircularProgress.defaultProps.endColor,
      isProgressWithGradient = CircularProgress.defaultProps.isProgressWithGradient,
      tintColor,
      isFillerPadding=false
    } = this.props;

    const maxWidthCircle = backgroundWidth ? Math.max(width, backgroundWidth) : width;
    const sizeValue = typeof size === 'number' ? size : (size as unknown as { _value: number })._value;
    const sizeWithPadding = sizeValue / 2 + padding / 2;
    const radius = sizeValue / 2 - maxWidthCircle / 2 - padding / 2;

    const clampedFill = this.clampFill(fill);
    const currentFillAngle = (arcSweepAngle * clampedFill) / 100;
    const coordinate = this.polarToCartesian(
      sizeWithPadding,
      sizeWithPadding,
      radius,
      currentFillAngle
    );
    const cap = renderCap ? renderCap({ center: coordinate }) : null;

    const offset = sizeValue - maxWidthCircle * 2;

    const localChildrenContainerStyle: ViewStyle = {
      position: 'absolute',
      left: maxWidthCircle + padding / 2,
      top: maxWidthCircle + padding / 2,
      width: offset,
      height: offset,
      borderRadius: offset / 2,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      overflow: 'hidden',
      ...childrenContainerStyle,
    };

    // --- Conic Gradient Arc Drawing ---
    const SEGMENTS = 60;
    const startColor = hexToRgb(beginColor ?? CircularProgress.defaultProps.beginColor);
    const lastColor = hexToRgb(endColor ?? CircularProgress.defaultProps.endColor);
    const anglePerSegment = arcSweepAngle / SEGMENTS;
    const filledSegments = Math.floor((clampedFill / 100) * SEGMENTS);
    const arcPaths = [];
    for (let i = 0; i < filledSegments; i++) {
      const startAngle = i * anglePerSegment;
      const endAngle = (i + 1) * anglePerSegment;
      const t = i / (SEGMENTS - 1);
      const color = rgbToHex(interpolateColor(startColor, lastColor, t));

      arcPaths.push(
        <Path
          key={`arc-segment-${i}`}
          d={this.circlePath(sizeWithPadding, sizeWithPadding, radius, startAngle, endAngle)}
          stroke={color}
          strokeWidth={isFillerPadding?width-2:width}
          strokeLinecap={fillLineCap}
          fill="transparent"
        />
      );
    }
    // --- End Conic Gradient Arc Drawing ---

    const strokeDasharrayBackground = dashedBackground.gap > 0
      ? Object.values(dashedBackground).map(value => Number(value))
      : undefined;

    const strokeDasharrayTint = dashedTint.gap > 0
      ? Object.values(dashedTint).map(value => Number(value))
      : undefined;

    const circlePath = this.circlePath(
        sizeWithPadding,
        sizeWithPadding,
        radius,
        0,
        currentFillAngle
      );

    return (
      <View style={style}>
        <Svg width={sizeValue + padding} height={sizeValue + padding}>
          <G 
            rotation={rotation} 
            originX={(sizeValue + padding) / 2} 
            originY={(sizeValue + padding) / 2}
          >
            {/* Background Circle */}
            {backgroundColor && (
              <Path
                d={this.circlePath(
                  sizeWithPadding,
                  sizeWithPadding,
                  radius,
                  0,
                  360
                )}
                stroke={backgroundColor}
                strokeWidth={backgroundWidth || width}
                strokeLinecap={lineCap}
                strokeDasharray={strokeDasharrayBackground}
                fill="transparent"
              />
            )}
            {/* Conic Gradient Progress Segments */}
            {arcPaths}
            {/* 3D Highlight at the top */}
            {clampedFill > 0 && (
              <Path
                d={this.circlePath(
                  sizeWithPadding,
                  sizeWithPadding,
                  radius, // slightly inside
                  0,
                  currentFillAngle>2? currentFillAngle-2:currentFillAngle
                )}
                stroke="white"
                strokeWidth={width * 0.30}
                strokeLinecap="round"
                fill="none"
                opacity={0.25}
              />
            )}
            {/* 3D Shadow at the bottom */}
            {clampedFill > 0 && (
              <Path
                d={this.circlePath(
                  sizeWithPadding,
                  sizeWithPadding,
                  radius + width * 0.35, // slightly outside
                  0,
                  currentFillAngle>2? currentFillAngle-2:currentFillAngle
                )}
                stroke="#000"
                strokeWidth={width * 0.15}
                strokeLinecap="round"
                fill="none"
                opacity={0.25}
              />
            )}
              {fill > 0 && !isProgressWithGradient && (
              <Path
                d={circlePath}
                stroke={tintColor}
                strokeWidth={width}
                strokeLinecap={fillLineCap}
                strokeDasharray={strokeDasharrayTint}
                fill="transparent"
              />
            )}
            {cap}
          </G>
        </Svg>
        {children && <View style={localChildrenContainerStyle}>{children(clampedFill)}</View>}
      </View>
    );
  }

  static propTypes = {
    style: PropTypes.object,
    size: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.instanceOf(Animated.Value),
    ]).isRequired,
    fill: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    backgroundWidth: PropTypes.number,
    tintColor: PropTypes.string,
    tintTransparency: PropTypes.bool,
    backgroundColor: PropTypes.string,
    rotation: PropTypes.number,
    lineCap: PropTypes.string,
    arcSweepAngle: PropTypes.number,
    children: PropTypes.func,
    childrenContainerStyle: PropTypes.object,
    padding: PropTypes.number,
    renderCap: PropTypes.func,
    dashedBackground: PropTypes.object,
    dashedTint: PropTypes.object,
    isProgressWithGradient: PropTypes.bool,
  } as const;

  static defaultProps = {
    tintColor: 'black',
    tintTransparency: true,
    rotation: 180,
    lineCap: 'butt' as const,
    arcSweepAngle: 360,
    padding: 0,
    dashedBackground: { width: 0, gap: 0 },
    dashedTint: { width: 0, gap: 0 },
    beginColor: '#CAE5ED', //light blue
    endColor: '#009FE8', //dark blue
    isProgressWithGradient: false,
  } as const;
}

